// PR-J — per-TPS assigned-loans drawer (opened from the oversight directory).
// Lists the loans assigned to a servicer with their acceptance status and
// pending counts, and offers the two switching paths: orderly Reassign
// (Loans.Manage, reuses the existing useReassignLoanTps hook) and break-glass
// Emergency switch (Portfolio.EmergencySwitchTps, opens the dedicated modal).

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  FormField,
  Skeleton,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  useToast,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useTpsAssignedLoans } from "@/hooks/portfolioAdmin/useTpsOversight";
import {
  useReassignLoanTps,
  useTpsList,
  useTpsReadiness,
} from "@/hooks/portfolioAdmin/useTps";
import { useEntityNameMap } from "@/hooks/capitalMarkets/useCounterpartyNames";
import {
  buildReadinessMap,
  tpsOptionDisabled,
  tpsOptionLabel,
} from "@/lib/tpsPicker";
import { EmergencyTpsSwitchModal } from "./EmergencyTpsSwitchModal";
import type { TpsAssignedLoanDto } from "@/api/portfolioAdmin/tpsOversightApi";
import type { TpsDto } from "@/types/tps";

function acceptanceTone(status: string): "gold" | "finalized" | "error" {
  if (status === "Accepted") return "finalized";
  if (status === "Declined") return "error";
  return "gold";
}

export function TpsLoansDrawer({
  tpsId,
  tpsName,
  open,
  onClose,
}: {
  tpsId: string;
  tpsName: string;
  open: boolean;
  onClose: () => void;
}) {
  const { hasPermission } = useAuth();
  const canReassign = hasPermission("Loans.Manage");
  const canEmergency = hasPermission("Portfolio.EmergencySwitchTps");
  const query = useTpsAssignedLoans(tpsId, open);

  const [reassignLoan, setReassignLoan] =
    useState<TpsAssignedLoanDto | null>(null);
  const [emergencyLoan, setEmergencyLoan] =
    useState<TpsAssignedLoanDto | null>(null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Loans & switching — ${tpsName}`}
      description="Loans assigned to this servicer, with orderly reassignment and emergency switching."
      size="lg"
      footer={
        <Button variant="text" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div data-testid="tps-loans-drawer">
        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError ? (
          <p className="text-small text-status-error" role="alert">
            Couldn't load assigned loans. Try again.
          </p>
        ) : !query.data || query.data.length === 0 ? (
          <EmptyState
            title="No loans assigned"
            description="No loans are currently assigned to this servicer."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Acceptance</TH>
                <TH className="text-right">Pending bills</TH>
                <TH className="text-right">Pending dists</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {query.data.map((loan) => (
                <TR
                  key={loan.loanId}
                  data-testid={`tps-loan-row-${loan.loanId}`}
                >
                  <TD className="font-semibold text-onyx">
                    {loan.loanNumber}
                  </TD>
                  <TD>
                    <Badge status={acceptanceTone(loan.acceptanceStatus)}>
                      {loan.acceptanceStatus}
                    </Badge>
                  </TD>
                  <TD className="text-right tabular-nums">
                    {loan.pendingBillCount}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {loan.pendingDistributionCount}
                  </TD>
                  <TD className="text-right">
                    <div className="inline-flex items-center justify-end gap-space-2">
                      {canReassign && (
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => setReassignLoan(loan)}
                          data-testid={`tps-loan-reassign-${loan.loanId}`}
                        >
                          Reassign
                        </Button>
                      )}
                      {canEmergency && (
                        <Button
                          variant="text"
                          size="sm"
                          className="text-status-error"
                          onClick={() => setEmergencyLoan(loan)}
                          data-testid={`tps-loan-emergency-${loan.loanId}`}
                        >
                          Emergency switch
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {reassignLoan && (
        <ReassignLoanModal
          loanId={reassignLoan.loanId}
          loanNumber={reassignLoan.loanNumber}
          currentTpsId={tpsId}
          onClose={() => setReassignLoan(null)}
        />
      )}
      {emergencyLoan && (
        <EmergencyTpsSwitchModal
          loanId={emergencyLoan.loanId}
          loanNumber={emergencyLoan.loanNumber}
          currentTpsId={tpsId}
          open
          onClose={() => setEmergencyLoan(null)}
        />
      )}
    </Modal>
  );
}

function ReassignLoanModal({
  loanId,
  loanNumber,
  currentTpsId,
  onClose,
}: {
  loanId: string;
  loanNumber: string;
  currentTpsId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const reassign = useReassignLoanTps(loanId);
  const listQuery = useTpsList({ status: "Active", pageSize: 200 });
  const { map: entityNames } = useEntityNameMap();
  const options = useMemo<TpsDto[]>(
    () => (listQuery.data?.data ?? []).filter((t) => t.id !== currentTpsId),
    [listQuery.data, currentTpsId],
  );

  const readinessQuery = useTpsReadiness();
  const readiness = useMemo(
    () => buildReadinessMap(readinessQuery.data),
    [readinessQuery.data],
  );

  const [newTpsId, setNewTpsId] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const selected = options.find((t) => t.id === newTpsId);

  const submit = () => {
    if (!newTpsId) {
      toast.error("Select a TPS provider first.");
      return;
    }
    setSubmitError(null);
    reassign
      .mutateAsync({ tpsId: newTpsId })
      .then(() => {
        toast.success("TPS assignment updated.");
        onClose();
      })
      .catch((err) => setSubmitError(extractErrorMessage(err)));
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Reassign TPS — loan ${loanNumber}`}
      description="Choose a new Third-Party Servicer for this loan."
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={reassign.isPending}
            disabled={!newTpsId}
            onClick={submit}
            data-testid="tps-loan-reassign-submit"
          >
            Reassign
          </Button>
        </div>
      }
    >
      <FormField label="TPS provider" required>
        <Select
          value={newTpsId}
          onChange={(e) => {
            setNewTpsId(e.target.value);
            setSubmitError(null);
          }}
          aria-label="TPS provider"
        >
          <option value="">Select a TPS…</option>
          {options.map((t) => (
            <option
              key={t.id}
              value={t.id}
              disabled={tpsOptionDisabled(t, readiness)}
            >
              {tpsOptionLabel(t, entityNames.get(t.entityId), readiness)}
            </option>
          ))}
        </Select>
      </FormField>
      {selected?.isDefault && (
        <p
          className="text-small text-gray-mid m-0 mt-space-2"
          data-testid="tps-unassign-note"
        >
          This removes the servicer from the loan — billing pauses until a new
          TPS is assigned.
        </p>
      )}
      {submitError && (
        <p
          className="text-small text-status-error m-0 mt-space-2"
          role="alert"
          data-testid="tps-loan-reassign-error"
        >
          {submitError}
          {selected && !selected.isDefault && (
            <>
              {" "}
              <Link
                to={`/portfolio-admin/tps/${selected.id}`}
                className="text-gold-shadow underline"
              >
                Open TPS setup
              </Link>
            </>
          )}
        </p>
      )}
    </Modal>
  );
}
